/**
 * Admin V3 — Compliance — Drivers Awaiting Review (G11.2)
 *
 * Complements the per-document PendingVerificationsQueue with a
 * per-DRIVER grouping. Owner G11.2 acceptance criterion:
 *   "see all drivers awaiting review"
 *
 * Data source: same `pending_verifications` array fetched by the
 * Compliance Pilot Page from /admin/compliance/dashboard. No additional
 * backend round-trip — we group on the client.
 *
 * Each row: driver_name + email | N pending docs | doctype badges |
 *           "Open dossier" deep-link to /admin-v3/drivers/:driverId.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ExternalLink } from 'lucide-react';
import { SectionCard, EmptyState } from '../../shared/visualTokens';
import { t } from '../../i18n';

const DriversAwaitingReviewPanel = ({ pending = [], locale = 'fr' }) => {
  const navigate = useNavigate();

  // Group the flat per-doc queue by driver_id.
  const grouped = useMemo(() => {
    const byDriver = new Map();
    for (const p of (pending || [])) {
      const key = p.driver_id;
      if (!key) continue;
      if (!byDriver.has(key)) {
        byDriver.set(key, {
          driver_id:    key,
          driver_name:  p.driver_name || key,
          driver_email: p.driver_email || null,
          documents:    [],
        });
      }
      byDriver.get(key).documents.push(p);
    }
    return Array.from(byDriver.values()).sort(
      (a, b) => b.documents.length - a.documents.length,
    );
  }, [pending]);

  if (grouped.length === 0) {
    return (
      <SectionCard
        icon={Users}
        iconClass="bg-purple-100 text-purple-700"
        title={t('compliance.drivers_awaiting.title', locale)}
      >
        <EmptyState
          icon={Users}
          title={t('compliance.drivers_awaiting.empty.title', locale)}
          desc={t('compliance.drivers_awaiting.empty.desc', locale)}
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      icon={Users}
      iconClass="bg-purple-100 text-purple-700"
      title={`${t('compliance.drivers_awaiting.title', locale)} (${grouped.length})`}
    >
      <div className="overflow-x-auto" data-testid="drivers-awaiting-review-panel">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-600">
              <th className="py-2 pr-4">{t('compliance.drivers_awaiting.column.driver', locale)}</th>
              <th className="py-2 pr-4">{t('compliance.drivers_awaiting.column.pending', locale)}</th>
              <th className="py-2 pr-4">{t('compliance.drivers_awaiting.column.types', locale)}</th>
              <th className="py-2 pr-4">{t('compliance.drivers_awaiting.column.action', locale)}</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <tr
                key={g.driver_id}
                className="border-b border-slate-100"
                data-testid={`drivers-awaiting-row-${g.driver_id}`}
              >
                <td className="py-2 pr-4">
                  <div className="text-slate-900">{g.driver_name}</div>
                  {g.driver_email && (
                    <div className="text-xs text-slate-500">{g.driver_email}</div>
                  )}
                </td>
                <td className="py-2 pr-4 font-mono text-slate-900">
                  {g.documents.length}
                </td>
                <td className="py-2 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {g.documents.map((d) => (
                      <span
                        key={d.id}
                        className="inline-flex items-center px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-xs"
                        title={`v${d.version} — ${d.uploaded_at || ''}`}
                      >
                        {t(`compliance.doctype.${d.document_type}`, locale)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-2 pr-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/drivers/${g.driver_id}`)}
                    className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                    data-testid={`drivers-awaiting-open-${g.driver_id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t('compliance.drivers_awaiting.action.open', locale)}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

export default DriversAwaitingReviewPanel;
